//@ts-nocheck
import React from 'react'
import { cbModal } from '../../../../../../../index'
import { useSlateStatic } from 'slate-react'
import ImageModal from './Modal/ModalComponent'
import UploadModal from './Modal/UploadModal'
import { setOldSelection } from '../../../../utils/setOldSelection'

/**
 * Opens the asset selection modal for choosing images from the asset library.
 * This function is used in RichTextEditor and selects the appropriate modal based on the type.
 * 
 * Type-based modal selection:
 * - type === 'asset': Uses customEmbedAssetsModal (for "Embed → Asset" menu option)
 * - other types: Uses customChooseFromAssetsModal (for "Choose from uploads" menu option)
 * 
 * @param {Event} e - The mouse/keyboard event
 * @param {Editor} editor - The Slate editor instance
 * @param {string} type - The type of asset being selected ('asset' for embed, 'image' for uploads)
 */
const openChooseFromImageModal = (e, editor, type) => {
  e.preventDefault()
  
  // Select the appropriate custom modal based on the type:
  // 'asset' type uses customEmbedAssetsModal, all other types use customChooseFromAssetsModal
  const CustomModal = type === 'asset' ? editor.customEmbedAssetsModal : editor.customChooseFromAssetsModal
  
  // Validate that CustomModal is a valid React component (function component or class component)
  if (CustomModal && (typeof CustomModal === 'function' || typeof CustomModal === 'object')) {
    // Render the custom modal provided by the consumer
    cbModal({
      component: (modalProps) => <CustomModal editor={editor} type={type} {...modalProps} />,
      modalProps: {
        size: 'max',
        shouldReturnFocusAfterClose: false,
        customClass: 'reference__modal scrte-reference__modal'
      },
    })
  } else {
    // Fallback to the default ImageModal component for asset selection
    cbModal({
      component: (modalProps) => <ImageModal editor={editor} type={type} {...modalProps} />,
      modalProps: {
        size: 'max',
        shouldReturnFocusAfterClose: false,
        customClass: 'reference__modal scrte-reference__modal'
      },
    })
  }
}
export const ImageOverlay = (props) => {
  const editor = useSlateStatic()

  /**
   * Handles mouse click events on the image overlay button.
   * Determines which modal to display based on type and available configurations.
   * 
   * Priority order:
   * 1. Custom modal (type-dependent: customEmbedAssetsModal or customChooseFromAssetsModal) - Consumer-provided modal component
   * 2. V2 Embed Picker (editor.showAssetEmbedModal) - New embed picker interface
   * 3. Default modal - Built-in ImageModal component
   */
  const handleMouseDown = (e, { editor }) => {
    const type = props.type
    // Select the appropriate custom modal: 'asset' type uses customEmbedAssetsModal, others use customChooseFromAssetsModal
    const CustomModal = type === 'asset' ? editor.customEmbedAssetsModal : editor.customChooseFromAssetsModal
    
    // Priority 1: Use consumer-provided custom modal if available (type-dependent)
    if (CustomModal && (typeof CustomModal === 'function' || typeof CustomModal === 'object')) {
      e.preventDefault()
      return cbModal({
        component: (modalProps) => <CustomModal editor={editor} type={type} {...modalProps} />,
        modalProps: {
          size: 'max',
          shouldReturnFocusAfterClose: false,
          customClass: 'reference__modal scrte-reference__modal'
        },
      })
    }
    
    // Priority 2: Use V2 embed picker if feature flag is enabled and modal handler exists
    if (editor.useV2EmbedPicker && editor.showAssetEmbedModal) {
      return editor.showAssetEmbedModal({...props})
    }
    
    // Priority 3: Fallback to default asset selection modal
    openChooseFromImageModal(e, editor, props.type)
  }

  return (
    <React.Fragment>
      {props.children({
        handleMouseDown: handleMouseDown,
        additionalParams: { editor },
      })}
    </React.Fragment>
  )
}

export const shortcutOpenChooseFromImageModal = (event, editor, format, registryObj, type) => {
  openChooseFromImageModal(event, editor, type)
}

export const UploadOverlay = (props) => {
  const editor = useSlateStatic();

  const handleMouseDown = (e, { editor }) => {
    e.preventDefault()
    if (typeof editor.openAMV2UploadTarget === 'function') {
      editor.openAMV2UploadTarget(editor)
      return
    }
    cbModal({
      component: (props) => <UploadModal editor={editor} {...props} />,
      modalProps: {
        size: 'max',
        shouldReturnFocusAfterClose: false,
        customClass: 'reference__modal scrte-reference__modal',
        onClose: () => editor?.requestProps?.onAutoDraftModalClose?.()
      },
    })
  }

  return (
    <React.Fragment>
      {props.children({
        handleMouseDown: handleMouseDown,
        additionalParams: { editor },
      })}
    </React.Fragment>
  )
}
